import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

const features = [
  {
    icon: 'ri-robot-2-line',
    title: 'Chat Inteligente',
    description: 'Nuestro asistente entiende lenguaje natural y responde al instante sobre canchas, horarios y precios.'
  },
  {
    icon: 'ri-time-line',
    title: 'Disponible 24/7',
    description: 'No importa la hora ni el día. El asistente siempre está listo para atenderte sin esperas.'
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'Reservas Automáticas',
    description: 'Solo dile qué cancha quieres, día y hora. El asistente hace la reserva por ti en segundos.'
  },
  {
    icon: 'ri-search-eye-line',
    title: 'Búsqueda Inteligente',
    description: 'Encuentra canchas por zona, precio, tipo de césped o disponibilidad con una simple conversación.'
  },
  {
    icon: 'ri-price-tag-3-line',
    title: 'Comparador de Precios',
    description: 'Pregunta por las mejores opciones y el asistente te muestra un comparativo con precios y ratings.'
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Soporte y Ayuda',
    description: '¿Problemas con un pago? ¿Dudas sobre PokTaToken? El asistente resuelve todo al momento.'
  }
];

const faqData = [
  {
    q: '¿Cómo funciona el asistente de IA?',
    a: 'El asistente usa inteligencia artificial avanzada para entender tus preguntas en lenguaje natural. Solo escribe o habla como si estuvieras chateando con un amigo, y él te responderá con la información que necesitas sobre canchas, reservas, precios y más.'
  },
  {
    q: '¿Puedo hacer reservas directamente con el asistente?',
    a: '¡Sí! Solo dile al asistente qué cancha quieres, la fecha, hora y duración. Él verificará disponibilidad y completará la reserva por ti. También puede ayudarte con pagos en efectivo o con PokTaToken.'
  },
  {
    q: '¿El asistente habla español?',
    a: 'Sí, el asistente está entrenado principalmente en español y entiende perfectamente modismos y expresiones locales mexicanas. También puede comunicarse en inglés si lo necesitas.'
  },
  {
    q: '¿Está disponible en todas las páginas?',
    a: 'El asistente está disponible en todo el sitio de PokTaPok. Solo haz clic en el ícono flotante de chat en la esquina inferior derecha de cualquier página para empezar a conversar.'
  },
  {
    q: '¿Qué tipo de preguntas puedo hacer?',
    a: 'Prácticamente de todo: buscar canchas por zona, comparar precios, ver horarios disponibles, conocer promociones, resolver dudas sobre PokTaToken, obtener ayuda con tu cuenta, y mucho más.'
  },
  {
    q: '¿El asistente guarda mi información?',
    a: 'Tus conversaciones son privadas y seguras. Solo guardamos la información necesaria para completar tus reservas. No compartimos tus datos con terceros y cumplimos con todas las normas de privacidad.'
  }
];

export default function AsistenteIA() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-green-900 via-green-800 to-emerald-900 text-white py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]">
          <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=Abstract%20AI%20neural%20network%20visualization%20with%20glowing%20interconnected%20nodes%20on%20dark%20green%20background%2C%20futuristic%20chatbot%20interface%20concept%2C%20digital%20brain%20patterns%20with%20soft%20green%20and%20teal%20neon%20accents%2C%20minimal%20tech%20background%20with%20geometric%20data%20flow%20lines%2C%20clean%20and%20modern%20artificial%20intelligence%20concept%20art&width=1600&height=900&seq=ai-hero-bg&orientation=landscape')] bg-cover bg-center"></div>
        </div>
        <div className="absolute top-20 right-10 opacity-20">
          <div className="w-64 h-64 rounded-full bg-green-400 blur-3xl"></div>
        </div>
        <div className="absolute bottom-10 left-10 opacity-20">
          <div className="w-48 h-48 rounded-full bg-emerald-300 blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-5 py-2 mb-6">
              <div className="w-3 h-3 rounded-full bg-green-400 animate-pulse"></div>
              <span className="text-sm font-medium text-green-100">En línea - 24/7</span>
            </div>
            <h1 className="text-4xl md:text-7xl font-bold mb-6 leading-tight">
              Asistente IA
              <span className="block text-green-300 text-2xl md:text-3xl mt-3 font-normal">
                Atención 24 horas, los 7 días de la semana
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-green-100 mb-10 max-w-3xl mx-auto leading-relaxed">
              Tu asistente personal con inteligencia artificial. Responde preguntas, 
              busca canchas, compara precios y hace reservas por ti. 
              Siempre disponible, nunca duerme.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-white text-green-800 hover:bg-gray-100 text-lg px-8 py-4"
                onClick={() => {
                  const agentBtn = document.querySelector('[data-readdy-agent-trigger]');
                  if (agentBtn) {
                    (agentBtn as HTMLElement).click();
                  }
                }}
              >
                <i className="ri-chat-3-line mr-2 text-xl"></i>
                Hablar con el Asistente
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-green-800 text-lg px-8 py-4"
                onClick={() => navigate('/registro')}
              >
                <i className="ri-user-add-line mr-2 text-xl"></i>
                Crear Cuenta Gratis
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {[
              { value: '24/7', label: 'Disponible' },
              { value: '< 3 seg', label: 'Tiempo de respuesta' },
              { value: '10K+', label: 'Consultas atendidas' },
              { value: '98%', label: 'Satisfacción' }
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-green-700 mb-1">{stat.value}</div>
                <div className="text-sm text-gray-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 md:py-24 bg-gray-50/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              ¿Qué puede hacer por ti?
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Nuestro asistente de IA está diseñado para hacer tu experiencia 
              en PokTaPok más rápida, fácil y personalizada
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {features.map((feat, i) => (
              <div
                key={i}
                className="group bg-white rounded-2xl p-6 border border-gray-100 hover:border-green-200 transition-all hover:shadow-lg cursor-default"
              >
                <div className="w-14 h-14 rounded-2xl bg-green-100 flex items-center justify-center mb-5 group-hover:bg-green-600 transition-colors">
                  <i className={`${feat.icon} text-2xl text-green-600 group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">{feat.title}</h3>
                <p className="text-gray-600 leading-relaxed text-sm">{feat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Así de fácil es usarlo
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Tres pasos simples para que el asistente haga todo por ti
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                step: '01',
                icon: 'ri-message-3-line',
                title: 'Escribe o Habla',
                desc: 'Dile al asistente lo que necesitas: "Quiero una cancha de fútbol 7 para mañana a las 6pm cerca del centro". También puedes usar comandos de voz.'
              },
              {
                step: '02',
                icon: 'ri-brain-line',
                title: 'El Asistente Busca',
                desc: 'La IA procesa tu solicitud, busca entre todas las canchas disponibles, compara precios, horarios y distancia para darte las mejores opciones.'
              },
              {
                step: '03',
                icon: 'ri-check-double-line',
                title: 'Reserva Confirmada',
                desc: 'Eliges tu opción favorita y el asistente completa la reserva por ti. Recibirás confirmación al instante. Así de rápido.'
              }
            ].map((item, i) => (
              <div key={i} className="relative text-center group">
                <div className="text-8xl font-black text-gray-100 group-hover:text-green-100 transition-colors absolute -top-8 left-1/2 -translate-x-1/2 select-none">
                  {item.step}
                </div>
                <div className="relative pt-10">
                  <div className="w-20 h-20 rounded-full bg-green-600 flex items-center justify-center mx-auto mb-5">
                    <i className={`${item.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Chat Demo Preview */}
      <section className="py-16 md:py-20 bg-gray-50/50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Mira cómo funciona
              </h2>
              <p className="text-gray-600 text-lg">
                Una conversación típica con nuestro asistente
              </p>
            </div>
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              {/* Chat Header */}
              <div className="bg-green-700 px-5 py-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                  <i className="ri-robot-2-line text-white text-lg"></i>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Asistente PokTaPok</p>
                  <p className="text-green-200 text-xs">En línea · Responde al instante</p>
                </div>
              </div>
              {/* Chat Messages */}
              <div className="p-5 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
                    <i className="ri-user-line text-gray-500 text-sm"></i>
                  </div>
                  <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[80%]">
                    <p className="text-gray-700 text-sm">Hola, busco una cancha de fútbol 7 para este sábado por la tarde, cerca del centro de CDMX</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 flex-row-reverse">
                  <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center shrink-0">
                    <i className="ri-robot-2-line text-white text-sm"></i>
                  </div>
                  <div className="bg-green-50 rounded-2xl rounded-tr-sm px-4 py-3 max-w-[80%] border border-green-100">
                    <p className="text-gray-700 text-sm">¡Claro! Encontré 3 canchas de fútbol 7 disponibles este sábado cerca del centro:</p>
                    <div className="mt-2 space-y-2">
                      <div className="bg-white rounded-lg p-2 border border-green-200">
                        <p className="text-sm font-semibold text-gray-800">Cancha Deportivo Centro</p>
                        <p className="text-xs text-gray-500">Fútbol 7 · $450/hr · A 5 min del Zócalo</p>
                      </div>
                      <div className="bg-white rounded-lg p-2 border border-green-200">
                        <p className="text-sm font-semibold text-gray-800">Fútbol Arena Reforma</p>
                        <p className="text-xs text-gray-500">Fútbol 7 · $380/hr · A 10 min del Ángel</p>
                      </div>
                      <div className="bg-white rounded-lg p-2 border border-green-200">
                        <p className="text-sm font-semibold text-gray-800">Campo Condesa Sports</p>
                        <p className="text-xs text-gray-500">Fútbol 7 · $500/hr · Pasto sintético nuevo</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">¿Quieres que reserve alguna? También puedo filtrar por precio si prefieres.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
                    <i className="ri-user-line text-gray-500 text-sm"></i>
                  </div>
                  <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[80%]">
                    <p className="text-gray-700 text-sm">Reserva la de Reforma, de 5pm a 7pm. ¡Gracias!</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 flex-row-reverse">
                  <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center shrink-0">
                    <i className="ri-robot-2-line text-white text-sm"></i>
                  </div>
                  <div className="bg-green-50 rounded-2xl rounded-tr-sm px-4 py-3 max-w-[80%] border border-green-100">
                    <p className="text-gray-700 text-sm">¡Listo! Reserva confirmada en <strong>Fútbol Arena Reforma</strong> para el sábado de 5pm a 7pm. Total: $760 MXN. Recibirás la confirmación en tu correo. ¡Disfruta el partido!</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Preguntas Frecuentes
            </h2>
            <p className="text-gray-600 text-lg">
              Todo lo que necesitas saber sobre el asistente
            </p>
          </div>
          <div className="max-w-3xl mx-auto space-y-3">
            {faqData.map((faq, i) => (
              <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <span className="font-semibold text-gray-800 pr-4">{faq.q}</span>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-colors ${openFaq === i ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
                    <i className={`text-sm ${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 md:py-20 bg-green-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="w-20 h-20 rounded-full bg-green-600 flex items-center justify-center mx-auto mb-6">
              <i className="ri-robot-2-line text-4xl text-white"></i>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              ¿Listo para probarlo?
            </h2>
            <p className="text-green-100 text-lg mb-10 max-w-xl mx-auto">
              El asistente está disponible ahora mismo. Haz clic en el botón 
              de chat o crea tu cuenta para empezar a reservar canchas como nunca.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-white text-green-800 hover:bg-gray-100"
                onClick={() => {
                  const agentBtn = document.querySelector('[data-readdy-agent-trigger]');
                  if (agentBtn) {
                    (agentBtn as HTMLElement).click();
                  }
                }}
              >
                <i className="ri-chat-3-line mr-2"></i>
                Abrir Asistente
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-green-800"
                onClick={() => navigate('/registro')}
              >
                <i className="ri-user-add-line mr-2"></i>
                Registrarme
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}